import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import CmsGallery from '../components/CmsGallery';
import ContactForm from '../components/ContactForm';
import { SERVICES_DATA, COMPANY_INFO } from '../data/companyData';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';

export default function ServiceDetailPage({ serviceSlugProp }) {
  const { slug } = useParams();
  const currentSlug = serviceSlugProp || slug;

  const service = SERVICES_DATA.find(
    (s) => s.slug === currentSlug || s.id === currentSlug
  );

  if (!service) {
    return <Navigate to="/404" replace />;
  }

  const pageTitle = service.metaTitle || `${service.title} | Morine Køl & Klimateknik`;
  const pageDescription = service.metaDescription || `${service.tagline}. Professionel rådgivning og driftssikre løsninger til erhverv fra Morine Køl & Klimateknik.`;

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} image={service.heroImage} />

      {/* Breadcrumb Bar */}
      <div className="bg-neutral-100/80 border-b border-neutral-200 text-xs py-2.5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2 text-neutral-600">
          <Link to="/" className="hover:text-neutral-900 transition-colors">
            Forside
          </Link>
          <span className="text-neutral-400">/</span>
          <Link to="/ydelser" className="hover:text-neutral-900 transition-colors">
            Ydelser
          </Link>
          <span className="text-neutral-400">/</span>
          <span className="font-semibold text-neutral-900">{service.title}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-neutral-950 text-white py-16 sm:py-20 border-b border-neutral-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Header info */}
            <div className="lg:col-span-7 space-y-5">
              <div>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 text-[#DFBF77] text-xs font-semibold uppercase tracking-wider shadow-xs">
                  <i className={service.icon} aria-hidden="true" />
                  <span>Ydelse & Klimateknik</span>
                </div>

                <h1 className="mt-4 pt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                  {service.title}
                </h1>
              </div>

              <p className="text-base sm:text-lg text-neutral-300 leading-relaxed max-w-2xl">
                {service.tagline}
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href="#kontaktform"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#C5A059] hover:bg-[#DFBF77] text-neutral-950 font-bold text-sm transition-all shadow-md"
                >
                  <span>Indhent tilbud på {service.shortTitle}</span>
                  <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
                </a>

                <a
                  href={COMPANY_INFO.phoneHref}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white font-semibold text-sm transition-all border border-neutral-700 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs text-[#DFBF77]" aria-hidden="true" />
                  <span className="whitespace-nowrap">{COMPANY_INFO.phoneFormatted}</span>
                </a>
              </div>
            </div>

            {/* Right Hero Image */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-neutral-800 shadow-2xl bg-neutral-900 aspect-[4/3]">
                <img
                  src={getOptimizedImageUrl(service.heroImage, { width: 900, height: 675 })}
                  alt={`${service.title} - Professionel installation`}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 sm:py-20 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-10">
              {/* Detailed Description */}
              <div>
                <span className="inline-block text-xs font-bold text-[#8D6D2F] uppercase tracking-wider bg-[#FAF6ED] px-3.5 py-1.5 rounded-full border border-[#EAD5A5] shadow-xs">
                  Faglig ekspertise
                </span>
                <h2 className="mt-3.5 pt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-950">
                  Omfattende løsninger tilpasset din virksomhed
                </h2>
                <p className="mt-4 text-neutral-700 text-base sm:text-lg leading-relaxed">
                  {service.description}
                </p>
              </div>

              {/* Specific Features / Points */}
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FAF6ED] border border-[#EAD5A5] flex items-center justify-center text-[#8D6D2F] text-sm shrink-0">
                    <i className="fa-solid fa-list-check" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#8D6D2F]">
                      Ydelsens indhold
                    </span>
                    <h3 className="mt-1 pt-0.5 text-lg sm:text-xl font-extrabold text-neutral-900 leading-tight">
                      Hvad vi tilbyder inden for {service.title.toLowerCase()}:
                    </h3>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3.5">
                  {service.points.map((pt, idx) => (
                    <div
                      key={idx}
                      className="group relative flex items-start gap-4 p-4 sm:p-4.5 rounded-2xl bg-neutral-50 hover:bg-white border border-neutral-200/90 hover:border-[#8D6D2F]/40 shadow-xs hover:shadow-md transition-all duration-200"
                    >
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FAF6ED] to-[#F5EBD4] border border-[#EAD5A5] flex items-center justify-center shrink-0 text-[#8D6D2F] text-xs font-mono font-bold shadow-xs group-hover:scale-105 transition-transform mt-0.5">
                        {String(idx + 1).padStart(2, '0')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm sm:text-base font-semibold text-neutral-800 group-hover:text-neutral-950 transition-colors leading-snug block">
                          {pt}
                        </span>
                      </div>
                      <div className="w-5 h-5 rounded-full bg-neutral-200/60 group-hover:bg-[#FAF6ED] text-neutral-400 group-hover:text-[#8D6D2F] flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-all text-[10px]">
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Environmental / Energy benefits with green checkmarks ONLY */}
              <div className="relative rounded-3xl bg-neutral-950 text-white border border-neutral-800 p-6 sm:p-8 overflow-hidden shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#8D6D2F]/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-neutral-900 border border-neutral-800 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-leaf text-[#DFBF77] text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#DFBF77]">
                        Bæredygtighed & Økonomi
                      </span>
                      <h3 className="mt-1 pt-0.5 text-lg font-bold text-white">
                        Energi- og driftsmæssige fordele
                      </h3>
                    </div>
                  </div>

                  <p className="text-sm text-neutral-300 leading-relaxed">
                    Med korrekt rådgivning og dimensionering opnår du både bedre driftsøkonomi og reduceret miljøpåvirkning:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {service.environmentalPoints.map((env, eIdx) => (
                      <div
                        key={eIdx}
                        className="flex items-center gap-3 p-3 sm:p-3.5 rounded-xl bg-neutral-900/90 border border-neutral-800/90 hover:border-neutral-700 transition-colors"
                      >
                        <div className="w-5 h-5 rounded-full bg-emerald-950/80 border border-emerald-500/60 flex items-center justify-center flex-shrink-0">
                          <i className="fa-solid fa-check text-emerald-400 text-[10px]" aria-hidden="true" />
                        </div>
                        <span className="text-xs font-semibold text-neutral-200 leading-snug">{env}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CMS Gallery Component (Dynamic lookup via slug) */}
              <div className="pt-6 border-t border-neutral-200">
                <CmsGallery
                  slug={service.gallerySlug}
                  title={`Galleri: ${service.title}`}
                  description={`Eksempler på udført arbejde og komponenter inden for ${service.title.toLowerCase()}.`}
                />
              </div>
            </div>

            {/* Right Sidebar: Quick Facts + Embedded Contact Form */}
            <div className="lg:col-span-5 space-y-8">
              {/* Technical Specifications / Fact Box (Enhanced & Polished) */}
              <div className="rounded-3xl bg-white border border-neutral-200/90 shadow-lg shadow-neutral-950/5 overflow-hidden transition-all">
                {/* Header with gradient line and icon */}
                <div className="h-1.5 bg-gradient-to-r from-[#DFBF77] via-[#8D6D2F] to-[#DFBF77]" />
                <div className="p-6 sm:p-7 pb-4">
                  <div className="flex items-center gap-3.5 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-[#8D6D2F]/10 border border-[#8D6D2F]/20 flex items-center justify-center text-[#8D6D2F] shrink-0">
                      <i className="fa-solid fa-clipboard-list text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-[11px] font-bold tracking-wider uppercase text-[#8D6D2F] block">
                        Teknisk oversigt
                      </span>
                      <h3 className="text-base sm:text-lg font-extrabold text-neutral-900 leading-tight">
                        Faktaboks: {service.title}
                      </h3>
                    </div>
                  </div>

                  {/* Highlights Grid */}
                  <div className="space-y-2.5">
                    {service.technicalHighlights.map((tech, tIdx) => {
                      const getIcon = (label) => {
                        const l = label.toLowerCase();
                        if (l.includes('formål') || l.includes('fokus')) return 'fa-solid fa-bullseye';
                        if (l.includes('målgruppe') || l.includes('kunde')) return 'fa-solid fa-users-gear';
                        if (l.includes('metode') || l.includes('teknik')) return 'fa-solid fa-sliders';
                        if (l.includes('kapacitet') || l.includes('størrelse')) return 'fa-solid fa-gauge-high';
                        return 'fa-solid fa-circle-check';
                      };

                      return (
                        <div
                          key={tIdx}
                          className="p-3.5 rounded-xl bg-neutral-50/80 border border-neutral-200/70 flex items-start gap-3 transition-colors hover:bg-neutral-50"
                        >
                          <div className="w-7 h-7 rounded-lg bg-white border border-neutral-200/80 shadow-2xs flex items-center justify-center text-[#8D6D2F] shrink-0 mt-0.5">
                            <i className={`${getIcon(tech.label)} text-[11px]`} aria-hidden="true" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-extrabold text-neutral-400 uppercase tracking-wider block mb-0.5">
                              {tech.label}
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-neutral-800 leading-snug block">
                              {tech.value}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Geografi / Område */}
                    <div className="p-3.5 rounded-xl bg-neutral-50/80 border border-neutral-200/70 flex items-start gap-3 transition-colors hover:bg-neutral-50">
                      <div className="w-7 h-7 rounded-lg bg-white border border-neutral-200/80 shadow-2xs flex items-center justify-center text-[#8D6D2F] shrink-0 mt-0.5">
                        <i className="fa-solid fa-location-dot text-[11px]" aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-extrabold text-neutral-400 uppercase tracking-wider block mb-0.5">
                          Dækningsområde
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-neutral-800 leading-snug block">
                          Sjælland & Hovedstadsområdet
                        </span>
                      </div>
                    </div>

                    {/* Direkte Kontakt / Callout */}
                    <div className="mt-3 p-3.5 sm:p-4 rounded-xl bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-950 text-white border border-neutral-800 shadow-md flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#8D6D2F] text-white flex items-center justify-center shrink-0 shadow-xs">
                          <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-[#DFBF77] uppercase tracking-wider block">
                            Hurtig rådgivning
                          </span>
                          <a
                            href={COMPANY_INFO.phoneHref}
                            className="text-sm font-bold text-white hover:text-[#DFBF77] transition-colors whitespace-nowrap"
                          >
                            {COMPANY_INFO.phoneFormatted}
                          </a>
                        </div>
                      </div>
                      <a
                        href={COMPANY_INFO.phoneHref}
                        className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-[#8D6D2F] text-white border border-white/10 text-xs font-semibold transition-all whitespace-nowrap"
                      >
                        Ring op
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Embedded Contact Form anchored to #kontaktform */}
              <div id="kontaktform">
                <ContactForm defaultService={service.title} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services Navigation Bar */}
      <section className="py-12 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <h3 className="text-lg font-bold text-neutral-900">
              Se andre ydelser fra Morine Køl & Klimateknik
            </h3>
            <Link
              to="/ydelser"
              className="text-xs font-bold text-[#8D6D2F] hover:underline flex items-center gap-1"
            >
              <span>Se alle ydelser</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {SERVICES_DATA.filter((s) => s.slug !== service.slug).map((other) => (
              <Link
                key={other.slug}
                to={`/ydelser/${other.slug}`}
                className="p-3.5 rounded-xl bg-white border border-neutral-200 text-center group hover:border-[#C5A059] transition-all shadow-xs"
              >
                <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-neutral-100 group-hover:bg-[#FAF6ED] text-neutral-700 group-hover:text-[#8D6D2F] flex items-center justify-center text-xs transition-colors">
                  <i className={other.icon} aria-hidden="true" />
                </div>
                <span className="text-xs font-semibold text-neutral-800 group-hover:text-[#8D6D2F] block leading-tight">
                  {other.shortTitle}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
